import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ListPlus, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useHub } from "@/components/AppShell";
import { EmptyState, Loader, PageHeader } from "@/components/common";
import { Confirm } from "@/components/kids/kidsUi";
import { useLiveRefresh } from "@/lib/live";
import { TaskGroup } from "@/components/home/TaskRow";
import { DoneDialog, PresetsDialog, TaskDialog } from "@/components/home/TaskDialogs";
import Appliances from "@/components/home/Appliances";
import Seasonal from "@/components/home/Seasonal";
import Costs from "@/components/home/Costs";
import { writesLocked } from "@/lib/splits";

const ORANGE = "var(--glow-home)";

function Tasks({ hub, d, load }) {
  const [dlg, setDlg] = useState(null);
  const done = () => { setDlg(null); load(); };
  const snooze = (t, n) => api.post(`/organizations/${hub.id}/home/tasks/${t.id}/snooze`, { days: n }).then(() => { toast.success(`Snoozed ${n === 1 ? "1 day" : "1 week"}`); load(); }).catch((e) => toast.error(errMsg(e)));
  const del = () => api.delete(`/organizations/${hub.id}/home/tasks/${dlg.t.id}`).then(() => { toast.success("Task deleted"); done(); }).catch((e) => toast.error(errMsg(e)));
  const row = (t) => ({ onDone: () => setDlg({ k: "done", t }), onSnooze: (n) => snooze(t, n), onEdit: () => setDlg({ k: "edit", t }), onDelete: () => setDlg({ k: "del", t }) });
  const empty = !["overdue", "soon", "upcoming", "completed"].some((g) => d.groups[g].length);
  return (
    <div data-testid="tasks-tab">
      <div className="mb-5 flex flex-wrap gap-2">
        <button className="q-btn q-btn-primary min-h-[44px]" onClick={() => setDlg({ k: "edit" })} data-testid="task-add-btn"><Plus className="h-4 w-4" /> Add task</button>
        <button className="q-btn q-btn-secondary min-h-[44px]" onClick={() => setDlg({ k: "presets" })} data-testid="task-presets-btn"><ListPlus className="h-4 w-4" /> Add from presets</button>
      </div>
      {empty && <EmptyState title="No home care tasks yet" hint="Start with presets like HVAC filters, smoke alarms and oil changes." rgb={ORANGE} testid="tasks-empty" />}
      {["overdue", "soon", "upcoming", "completed"].map((g) => <TaskGroup key={g} g={g} rows={d.groups[g]} today={d.today} canDelete={d.can_delete} row={row} />)}
      {dlg?.k === "edit" && <TaskDialog hubId={hub.id} d={d} task={dlg.t} onClose={() => setDlg(null)} onSaved={done} />}
      {dlg?.k === "done" && <DoneDialog hubId={hub.id} d={d} task={dlg.t} onClose={() => setDlg(null)} onSaved={done} />}
      {dlg?.k === "presets" && <PresetsDialog hubId={hub.id} d={d} onClose={() => setDlg(null)} onSaved={done} />}
      <Confirm open={dlg?.k === "del"} onClose={() => setDlg(null)} title={`Delete “${dlg?.t?.title}”?`} body="Its history goes too." onConfirm={del} testid="task-delete-confirm" />
    </div>
  );
}

export default function HomeCare() {
  const { hub, reloadHub } = useHub();
  const [params, setParams] = useSearchParams();
  const tab = ["appliances", "seasonal", "costs"].includes(params.get("tab")) ? params.get("tab") : "tasks";
  const [d, setD] = useState(null);
  const [v, setV] = useState(0);
  const reloadRef = useRef(reloadHub);
  reloadRef.current = reloadHub;
  const load = useCallback(() => api.get(`/organizations/${hub.id}/home/tasks`).then(({ data }) => { setD(data); reloadRef.current?.(); }).catch((e) => toast.error(errMsg(e))), [hub.id]);
  useEffect(() => { load(); }, [load]);
  useLiveRefresh(() => { load(); setV((x) => x + 1); }, (m) => m.entity === "home", 20000);
  if (!d) return <Loader />;
  return (
    <div data-testid="home-care-page">
      <PageHeader eyebrow="Home" title="Home care" rgb={ORANGE} testid="home-care-title" />
      <div className="mb-5 flex max-w-full gap-1 overflow-x-auto rounded-full border border-q-line p-1" role="tablist">
        {[["tasks", "Tasks"], ["seasonal", "Seasonal"], ["costs", "Providers & costs"], ["appliances", "Appliances"]].map(([id, l]) => (
          <button key={id} role="tab" aria-selected={tab === id} className={`min-h-[44px] shrink-0 whitespace-nowrap rounded-full px-4 text-sm font-semibold transition-colors duration-150 ${tab === id ? "text-white" : "text-q-muted hover:text-q-text"}`}
            style={tab === id ? { background: `rgb(${ORANGE})` } : undefined} onClick={() => setParams(id === "tasks" ? {} : { tab: id })} data-testid={`home-care-tab-${id}`}>{l}</button>
        ))}
      </div>
      {tab === "tasks" && <Tasks hub={hub} d={d} load={load} />}
      {tab === "seasonal" && <Seasonal key={v} hub={hub} locked={writesLocked(hub)} />}
      {tab === "costs" && <Costs key={v} hub={hub} locked={writesLocked(hub)} today={d.today} />}
      {tab === "appliances" && <Appliances key={v} hub={hub} />}
    </div>
  );
}
